import { useEffect, useState } from 'react';
import type { Key } from 'react';
import type { TablePaginationConfig } from 'antd/lib/table';
import type { SortOrder } from 'antd/lib/table/interface';

import useLocale from '../../../hooks/useLocale';
import { INTEGRATION_DEFAULT_PAGE_SIZE, INTEGRATION_PAGE_SIZE_OPTIONS } from './constants';

interface ISorter {
  columnKey?: Key;
  order?: SortOrder;
}

interface IOptions<TFilters, TSort> {
  defaultFilters: TFilters;
  defaultSort: TSort;
  toSort: (columnKey: unknown, order?: SortOrder) => TSort;
}

export interface IListControls<TFilters, TSort> {
  filters: TFilters;
  sort: TSort;
  page: number;
  pageSize: number;
  selectedIds: string[];
  setPage: (page: number) => void;
  setSelectedIds: (ids: string[]) => void;
  changeFilters: (patch: Partial<TFilters>) => void;
  resetFilters: () => void;
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: unknown,
    sorter: ISorter | ISorter[],
    extra: { action: string }
  ) => void;
  getPagination: (total: number) => TablePaginationConfig;
}

// The state of a list that is filtered, sorted and paged on the server. Any change of filter, sort or page clears
// the selection (it is per page), and a new filter or sort starts again on the first page.
export const useListControls = <TFilters extends object, TSort>({
  defaultFilters,
  defaultSort,
  toSort,
}: IOptions<TFilters, TSort>): IListControls<TFilters, TSort> => {
  const { t } = useLocale();
  const [filters, setFilters] = useState<TFilters>(defaultFilters);
  const [sort, setSort] = useState<TSort>(defaultSort);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(INTEGRATION_DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const restartPaging = () => {
    setPage(1);
    setSelectedIds([]);
  };

  const changeFilters = (patch: Partial<TFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    restartPaging();
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    restartPaging();
  };

  const handleTableChange: IListControls<TFilters, TSort>['handleTableChange'] = (
    pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const [changedSorter] = Array.isArray(sorter) ? sorter : [sorter];
      setSort(toSort(changedSorter?.columnKey, changedSorter?.order));
      restartPaging();
      return;
    }
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? INTEGRATION_DEFAULT_PAGE_SIZE);
    setSelectedIds([]);
  };

  const getPagination = (total: number): TablePaginationConfig => ({
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    pageSizeOptions: INTEGRATION_PAGE_SIZE_OPTIONS,
    showTotal: (count) => `${t.TOTAL} ${count} ${t.ITEMS}`,
  });

  return {
    filters,
    sort,
    page,
    pageSize,
    selectedIds,
    setPage,
    setSelectedIds,
    changeFilters,
    resetFilters,
    handleTableChange,
    getPagination,
  };
};

// A page becomes empty when its last rows leave the list (commands cancelled, events replayed under a status filter).
export const useLeaveEmptyPage = (
  isPastLastPage: boolean,
  totalCount: number,
  pageSize: number,
  setPage: (page: number) => void
): void => {
  useEffect(() => {
    if (isPastLastPage) setPage(Math.max(1, Math.ceil(totalCount / pageSize)));
  }, [isPastLastPage, totalCount, pageSize, setPage]);
};
